import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import formatCurrency from "@/utils/formatCurrency";

const generatePDF = async (data, userName) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Calculate totals
    const totalIncome = data.incomes.reduce((sum, i) => sum + (i.is_monthly ? i.monthly_amount * 12 : i.yearly_amount), 0);
    const totalExpenses = data.expenses.reduce((sum, e) => sum + (e.is_monthly ? e.monthly_amount * 12 : e.yearly_amount), 0);
    const totalSavings = data.savings.reduce((sum, s) => sum + (s.is_monthly ? s.monthly_amount * 12 : s.yearly_amount), 0);
    const surplus = totalIncome - totalExpenses - totalSavings;

    // Calculate Assets (Income + Savings as assets)
    const totalAssets = totalIncome + totalSavings;
    // Calculate Liabilities (Expenses as liabilities)
    const totalLiabilities = totalExpenses;
    const netWorth = totalAssets - totalLiabilities;

    // ========== PAGE 1: HEADER & SUMMARY ==========
    
    // Header with company branding - Teal background
    doc.setFillColor(20, 184, 166);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    // Company Name
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.text("SubiWealth", 15, 22);
    
    // Tagline
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("Personal Finance Management", 15, 32);
    
    // Report Info - Right Side
    doc.setFontSize(10);
    const reportDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
    doc.text(`Report Date: ${reportDate}`, pageWidth - 15, 18, { align: 'right' });
    doc.text(`Prepared for: ${userName}`, pageWidth - 15, 26, { align: 'right' });
    doc.text("Annual Financial Report", pageWidth - 15, 34, { align: 'right' });

    let yPos = 55;

    // Report Title
    doc.setTextColor(20, 184, 166);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Comprehensive Financial Report", 15, yPos);
    
    yPos += 12;
    
    // Executive Summary
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    
    const summaryText = `This report provides a detailed overview of your annual financial position. ` +
      `Your total yearly income stands at Rs. ${formatCurrency(totalIncome)}, while your expenses amount to Rs. ${formatCurrency(totalExpenses)}. ` +
      `With savings of Rs. ${formatCurrency(totalSavings)}, ${surplus >= 0 
        ? `you have achieved a healthy surplus of Rs. ${formatCurrency(surplus)}, indicating strong financial management.` 
        : `there is a deficit of Rs. ${formatCurrency(Math.abs(surplus))} that requires attention.`}`;
    
    const splitSummary = doc.splitTextToSize(summaryText, pageWidth - 30);
    doc.text(splitSummary, 15, yPos);
    yPos += splitSummary.length * 6 + 12;

    // Cash Flow Summary Box
    doc.setFillColor(240, 253, 250);
    doc.roundedRect(15, yPos, pageWidth - 30, 50, 3, 3, 'F');
    doc.setDrawColor(20, 184, 166);
    doc.setLineWidth(0.5);
    doc.roundedRect(15, yPos, pageWidth - 30, 50, 3, 3, 'S');
    
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(20, 184, 166);
    doc.text("Cash Flow Summary (Annual)", 20, yPos + 12);
    
    // Summary values in two columns
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    
    // Left column
    doc.setTextColor(34, 197, 94);
    doc.text(`Total Income:`, 20, yPos + 26);
    doc.setFont("helvetica", "bold");
    doc.text(`Rs. ${formatCurrency(totalIncome)}`, 65, yPos + 26);
    
    doc.setFont("helvetica", "normal");
    doc.setTextColor(239, 68, 68);
    doc.text(`Total Expenses:`, 20, yPos + 38);
    doc.setFont("helvetica", "bold");
    doc.text(`Rs. ${formatCurrency(totalExpenses)}`, 65, yPos + 38);
    
    // Right column
    doc.setFont("helvetica", "normal");
    doc.setTextColor(59, 130, 246);
    doc.text(`Total Savings:`, pageWidth/2 + 5, yPos + 26);
    doc.setFont("helvetica", "bold");
    doc.text(`Rs. ${formatCurrency(totalSavings)}`, pageWidth/2 + 50, yPos + 26);
    
    doc.setFont("helvetica", "normal");
    doc.setTextColor(surplus >= 0 ? 20 : 239, surplus >= 0 ? 184 : 68, surplus >= 0 ? 166 : 68);
    doc.text(`Net Surplus:`, pageWidth/2 + 5, yPos + 38);
    doc.setFont("helvetica", "bold");
    doc.text(`Rs. ${formatCurrency(surplus)}`, pageWidth/2 + 50, yPos + 38);
    
    yPos += 60;

    // ========== ASSETS & LIABILITIES BREAKDOWN ==========
    doc.setTextColor(20, 184, 166);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Assets & Liabilities Overview", 15, yPos);
    yPos += 8;

    const assetLiabilityData = [
      ['Total Assets (Income + Savings)', `Rs. ${formatCurrency(totalAssets)}`],
      ['Total Liabilities (Expenses)', `Rs. ${formatCurrency(totalLiabilities)}`],
      ['Net Worth', `Rs. ${formatCurrency(netWorth)}`]
    ];

    autoTable(doc, {
      startY: yPos,
      head: [['Description', 'Amount (INR)']],
      body: assetLiabilityData,
      theme: 'grid',
      headStyles: { fillColor: [20, 184, 166], textColor: 255, fontStyle: 'bold', halign: 'center' },
      styles: { fontSize: 10, cellPadding: 5, halign: 'left' },
      columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 60, halign: 'right', fontStyle: 'bold' } },
      margin: { left: 15, right: 15 },
      tableWidth: 'auto'
    });
    yPos = doc.lastAutoTable.finalY + 15;

    // ========== INCOME STATEMENT ==========
    doc.setTextColor(20, 184, 166);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Income Statement (Inflows)", 15, yPos);
    yPos += 8;

    const incomeTableData = data.incomes.map(i => [
      i.category,
      i.is_monthly ? "Monthly" : "Yearly",
      `Rs. ${formatCurrency(i.is_monthly ? i.monthly_amount : i.yearly_amount)}`,
      `Rs. ${formatCurrency(i.is_monthly ? i.monthly_amount * 12 : i.yearly_amount)}`
    ]);
    incomeTableData.push(['TOTAL INCOME', '', '', `Rs. ${formatCurrency(totalIncome)}`]);

    autoTable(doc, {
      startY: yPos,
      head: [['Category', 'Frequency', 'Amount', 'Annual Total']],
      body: incomeTableData,
      theme: 'striped',
      headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9, cellPadding: 4 },
      columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 25, halign: 'center' }, 2: { cellWidth: 40, halign: 'right' }, 3: { cellWidth: 45, halign: 'right' } },
      margin: { left: 15, right: 15 },
      tableWidth: 'auto',
      didParseCell: function(data) {
        if (data.row.index === incomeTableData.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [220, 252, 231];
        }
      }
    });
    yPos = doc.lastAutoTable.finalY + 15;

    if (yPos > pageHeight - 100) { doc.addPage(); yPos = 20; }

    // ========== EXPENSE STATEMENT ==========
    doc.setTextColor(20, 184, 166);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Expense Statement (Outflows)", 15, yPos);
    yPos += 8;

    const expenseTableData = data.expenses.map(e => [
      e.category, e.subcategory, e.is_monthly ? "Monthly" : "Yearly",
      `Rs. ${formatCurrency(e.is_monthly ? e.monthly_amount * 12 : e.yearly_amount)}`
    ]);
    expenseTableData.push(['TOTAL EXPENSES', '', '', `Rs. ${formatCurrency(totalExpenses)}`]);

    autoTable(doc, {
      startY: yPos,
      head: [['Category', 'Item', 'Frequency', 'Annual Total']],
      body: expenseTableData,
      theme: 'striped',
      headStyles: { fillColor: [239, 68, 68], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 'auto' }, 2: { cellWidth: 22, halign: 'center' }, 3: { cellWidth: 40, halign: 'right' } },
      margin: { left: 15, right: 15 },
      tableWidth: 'auto',
      didParseCell: function(data) {
        if (data.row.index === expenseTableData.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [254, 226, 226];
        }
      }
    });
    yPos = doc.lastAutoTable.finalY + 15;

    if (yPos > pageHeight - 100) { doc.addPage(); yPos = 20; }

    // ========== SAVINGS STATEMENT ==========
    doc.setTextColor(20, 184, 166);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Savings & Investments Statement", 15, yPos);
    yPos += 8;

    const savingsTableData = data.savings.map(s => [
      s.category, s.is_monthly ? "Monthly" : "Yearly",
      `Rs. ${formatCurrency(s.is_monthly ? s.monthly_amount : s.yearly_amount)}`,
      `Rs. ${formatCurrency(s.is_monthly ? s.monthly_amount * 12 : s.yearly_amount)}`
    ]);
    savingsTableData.push(['TOTAL SAVINGS', '', '', `Rs. ${formatCurrency(totalSavings)}`]);

    autoTable(doc, {
      startY: yPos,
      head: [['Category', 'Frequency', 'Amount', 'Annual Total']],
      body: savingsTableData,
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9, cellPadding: 4 },
      columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 25, halign: 'center' }, 2: { cellWidth: 38, halign: 'right' }, 3: { cellWidth: 42, halign: 'right' } },
      margin: { left: 15, right: 15 },
      tableWidth: 'auto',
      didParseCell: function(data) {
        if (data.row.index === savingsTableData.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [219, 234, 254];
        }
      }
    });
    yPos = doc.lastAutoTable.finalY + 15;

    // ========== BUDGET PLANNER ==========
    if (data.monthly_salary > 0) {
      if (yPos > pageHeight - 100) { doc.addPage(); yPos = 20; }

      doc.setTextColor(20, 184, 166);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Budget Allocation Plan", 15, yPos);
      yPos += 5;
      
      doc.setFontSize(11);
      doc.setTextColor(60, 60, 60);
      doc.setFont("helvetica", "normal");
      doc.text(`Based on Monthly Salary: Rs. ${formatCurrency(data.monthly_salary)}`, 15, yPos + 5);
      yPos += 12;

      const budgetTableData = data.budget_buckets.map((b, index) => [
        index + 1, b.name, `${b.percentage}%`,
        `Rs. ${formatCurrency((data.monthly_salary * b.percentage) / 100)}`
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['#', 'Budget Bucket', 'Allocation %', 'Monthly Budget']],
        body: budgetTableData,
        theme: 'striped',
        headStyles: { fillColor: [20, 184, 166], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 10, cellPadding: 4 },
        columnStyles: { 0: { cellWidth: 12, halign: 'center' }, 1: { cellWidth: 'auto' }, 2: { cellWidth: 30, halign: 'center' }, 3: { cellWidth: 45, halign: 'right' } },
        margin: { left: 15, right: 15 },
        tableWidth: 'auto'
      });
      yPos = doc.lastAutoTable.finalY + 15;
    }

    // ========== PAGE 3: INVESTMENT ASSETS ==========
    doc.addPage();
    yPos = 20;
    
    doc.setFillColor(20, 184, 166);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("SubiWealth - Investment Assets Summary", 15, 16);

    yPos = 35;

    doc.setTextColor(20, 184, 166);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Investment Assets", 15, yPos);
    yPos += 12;

    // Debt Assets Section
    doc.setTextColor(59, 130, 246);
    doc.setFontSize(13);
    doc.setFont("helvetica", "bolditalic");
    doc.text("Debt Assets", 15, yPos);
    yPos += 8;

    const debtAssetsData = data.investment_assets?.debt_assets?.map(a => [a.name, `Rs. ${formatCurrency(a.amount || 0)}`]) || [];
    const totalDebtAssets = (data.investment_assets?.debt_assets || []).reduce((sum, a) => sum + (a.amount || 0), 0);
    debtAssetsData.push(['Total Debt Assets', `Rs. ${formatCurrency(totalDebtAssets)}`]);

    autoTable(doc, {
      startY: yPos, head: [['Asset Type', 'Value (INR)']], body: debtAssetsData, theme: 'striped',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9, cellPadding: 4 },
      columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 50, halign: 'right' } },
      margin: { left: 15, right: 15 }, tableWidth: 'auto',
      didParseCell: function(cellData) { if (cellData.row.index === debtAssetsData.length - 1) { cellData.cell.styles.fontStyle = 'bold'; cellData.cell.styles.fillColor = [219, 234, 254]; } }
    });
    yPos = doc.lastAutoTable.finalY + 12;

    // Equity Assets Section
    doc.setTextColor(34, 197, 94);
    doc.setFontSize(13);
    doc.setFont("helvetica", "bolditalic");
    doc.text("Equity Assets", 15, yPos);
    yPos += 8;

    const equityAssetsData = data.investment_assets?.equity_assets?.map(a => [a.name, `Rs. ${formatCurrency(a.amount || 0)}`]) || [];
    const totalEquityAssets = (data.investment_assets?.equity_assets || []).reduce((sum, a) => sum + (a.amount || 0), 0);
    equityAssetsData.push(['Total Equity Assets', `Rs. ${formatCurrency(totalEquityAssets)}`]);

    autoTable(doc, {
      startY: yPos, head: [['Asset Type', 'Value (INR)']], body: equityAssetsData, theme: 'striped',
      headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9, cellPadding: 4 },
      columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 50, halign: 'right' } },
      margin: { left: 15, right: 15 }, tableWidth: 'auto',
      didParseCell: function(cellData) { if (cellData.row.index === equityAssetsData.length - 1) { cellData.cell.styles.fontStyle = 'bold'; cellData.cell.styles.fillColor = [220, 252, 231]; } }
    });
    yPos = doc.lastAutoTable.finalY + 12;

    // Gold Assets Section
    doc.setTextColor(234, 179, 8);
    doc.setFontSize(13);
    doc.setFont("helvetica", "bolditalic");
    doc.text("Gold Assets", 15, yPos);
    yPos += 8;

    const goldAssetsData = data.investment_assets?.gold_assets?.map(a => [a.name, `Rs. ${formatCurrency(a.amount || 0)}`]) || [];
    const totalGoldAssets = (data.investment_assets?.gold_assets || []).reduce((sum, a) => sum + (a.amount || 0), 0);
    goldAssetsData.push(['Total Gold Assets', `Rs. ${formatCurrency(totalGoldAssets)}`]);

    autoTable(doc, {
      startY: yPos, head: [['Asset Type', 'Value (INR)']], body: goldAssetsData, theme: 'striped',
      headStyles: { fillColor: [234, 179, 8], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9, cellPadding: 4 },
      columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 50, halign: 'right' } },
      margin: { left: 15, right: 15 }, tableWidth: 'auto',
      didParseCell: function(cellData) { if (cellData.row.index === goldAssetsData.length - 1) { cellData.cell.styles.fontStyle = 'bold'; cellData.cell.styles.fillColor = [254, 249, 195]; } }
    });
    yPos = doc.lastAutoTable.finalY + 15;

    // Total Investment Assets Summary
    const totalInvestmentAssets = totalDebtAssets + totalEquityAssets + totalGoldAssets;
    
    doc.setFillColor(240, 253, 250);
    doc.roundedRect(15, yPos, pageWidth - 30, 35, 3, 3, 'F');
    doc.setDrawColor(20, 184, 166);
    doc.setLineWidth(1);
    doc.roundedRect(15, yPos, pageWidth - 30, 35, 3, 3, 'S');

    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    doc.setFont("helvetica", "normal");
    doc.text("Total Debt Assets:", 20, yPos + 10);
    doc.text(`Rs. ${formatCurrency(totalDebtAssets)}`, 90, yPos + 10);
    doc.text("Total Equity Assets:", 20, yPos + 20);
    doc.text(`Rs. ${formatCurrency(totalEquityAssets)}`, 90, yPos + 20);
    doc.text("Total Gold Assets:", 20, yPos + 30);
    doc.text(`Rs. ${formatCurrency(totalGoldAssets)}`, 90, yPos + 30);

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(20, 184, 166);
    doc.text("TOTAL INVESTMENT ASSETS:", pageWidth/2 + 10, yPos + 20);
    doc.setFontSize(14);
    doc.text(`Rs. ${formatCurrency(totalInvestmentAssets)}`, pageWidth - 20, yPos + 20, { align: 'right' });

    // ========== PAGE 4: LIABILITIES & NET WORTH ==========
    doc.addPage();
    yPos = 20;
    
    doc.setFillColor(20, 184, 166);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("SubiWealth - Liabilities & Net Worth", 15, 16);

    yPos = 35;

    doc.setTextColor(239, 68, 68);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Liabilities (Outstanding Loans)", 15, yPos);
    yPos += 10;

    const liabilitiesData = data.liabilities?.map(l => [l.name, `Rs. ${formatCurrency(l.amount || 0)}`]) || [];
    const totalLiabilitiesCalc = (data.liabilities || []).reduce((sum, l) => sum + (l.amount || 0), 0);
    liabilitiesData.push(['Total Liabilities', `Rs. ${formatCurrency(totalLiabilitiesCalc)}`]);

    autoTable(doc, {
      startY: yPos, head: [['Loan Type', 'Outstanding Balance (INR)']], body: liabilitiesData, theme: 'striped',
      headStyles: { fillColor: [239, 68, 68], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 10, cellPadding: 5 },
      columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 70, halign: 'right' } },
      margin: { left: 15, right: 15 }, tableWidth: 'auto',
      didParseCell: function(cellData) { if (cellData.row.index === liabilitiesData.length - 1) { cellData.cell.styles.fontStyle = 'bold'; cellData.cell.styles.fillColor = [254, 226, 226]; } }
    });
    yPos = doc.lastAutoTable.finalY + 20;

    // Net Worth Calculation Section
    const netWorthCalc = totalInvestmentAssets - totalLiabilitiesCalc;
    
    doc.setTextColor(20, 184, 166);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Net Worth Calculation", 15, yPos);
    yPos += 10;

    const netWorthData = [
      ['Total Investment Assets', `Rs. ${formatCurrency(totalInvestmentAssets)}`],
      ['Less: Total Liabilities', `Rs. ${formatCurrency(totalLiabilitiesCalc)}`],
      ['MY NET WORTH', `Rs. ${formatCurrency(netWorthCalc)}`]
    ];

    autoTable(doc, {
      startY: yPos, head: [['Description', 'Amount (INR)']], body: netWorthData, theme: 'grid',
      headStyles: { fillColor: [20, 184, 166], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 11, cellPadding: 6 },
      columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 70, halign: 'right' } },
      margin: { left: 15, right: 15 }, tableWidth: 'auto',
      didParseCell: function(cellData) {
        if (cellData.row.index === netWorthData.length - 1) {
          cellData.cell.styles.fontStyle = 'bold';
          cellData.cell.styles.fillColor = netWorthCalc >= 0 ? [220, 252, 231] : [254, 226, 226];
          cellData.cell.styles.textColor = netWorthCalc >= 0 ? [34, 197, 94] : [239, 68, 68];
        }
      }
    });
    yPos = doc.lastAutoTable.finalY + 20;

    // Net Worth Summary Box
    const boxColor = netWorthCalc >= 0 ? [220, 252, 231] : [254, 226, 226];
    const borderColor = netWorthCalc >= 0 ? [34, 197, 94] : [239, 68, 68];
    const textColor = netWorthCalc >= 0 ? [34, 197, 94] : [239, 68, 68];

    doc.setFillColor(...boxColor);
    doc.roundedRect(15, yPos, pageWidth - 30, 40, 5, 5, 'F');
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(2);
    doc.roundedRect(15, yPos, pageWidth - 30, 40, 5, 5, 'S');

    doc.setFontSize(14);
    doc.setTextColor(60, 60, 60);
    doc.setFont("helvetica", "normal");
    doc.text("My Net Worth = Total Investment Assets - Total Liabilities", pageWidth / 2, yPos + 15, { align: 'center' });

    doc.setFontSize(22);
    doc.setTextColor(...textColor);
    doc.setFont("helvetica", "bold");
    doc.text(`Rs. ${formatCurrency(netWorthCalc)}`, pageWidth / 2, yPos + 32, { align: 'center' });

    // ========== FOOTER ON ALL PAGES ==========
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setDrawColor(20, 184, 166);
      doc.setLineWidth(0.5);
      doc.line(15, pageHeight - 20, pageWidth - 15, pageHeight - 20);
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.setFont("helvetica", "normal");
      doc.text("SubiWealth - Your Trusted Financial Partner", 15, pageHeight - 12);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
      doc.text(`${userName} | ${new Date().toLocaleDateString('en-IN')}`, pageWidth - 15, pageHeight - 12, { align: 'right' });
    }

    doc.save(`SubiWealth_Financial_Report_${userName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    
    return true;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error("PDF Generation Error:", error);
    }
    alert("Error generating PDF: " + error.message);
    return false;
  }
};

export default generatePDF;
